import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import { apibaseurl } from "../webservice/Webservice_url";
import axios, { AxiosResponse } from "axios";
import { useAuth } from "../context/useAuthContext";
import Swal from "sweetalert2";

interface ApiOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  payload?: Record<string, any> | null;
  autoCall?: boolean;
  dependencies?: any[];
  responseType?: "json" | "blob" | "arraybuffer";
  handleGlobalError?: boolean;
  handleUnauthorized?: boolean;
}

interface UseApiResult<T> {
  data: T | null;
  loading: boolean;
  error: any;
  callApi: (
    payload?: Record<string, any> | FormData | null,
    customUrl?: string,
  ) => Promise<T | undefined>;
}

let isSessionExpiredAlertOpen = false;

function useApi<T = any>(
  endpoint: string,
  {
    method = "GET",
    payload = null,
    autoCall = true,
    dependencies = [],
    responseType = "json",
    handleGlobalError = true,
    handleUnauthorized = true,
  }: ApiOptions = {},
): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(autoCall);
  const [error, setError] = useState<any>(null);

  const navigate = useNavigate();
  const { auth } = useAuth();

  const getAuthHeader = () => {
    const token = auth?.token;

    return token
      ? { Authorization: `Bearer ${token}` }
      : {};
  };

  const callApi = useCallback(
    async (
      customPayload: Record<string, any> | FormData | null = payload,
      customUrl?: string,
    ): Promise<T | undefined> => {
      setLoading(true);

      try {
        const isFormData = customPayload instanceof FormData;

        const isCustomPayloadEmpty =
          !customPayload ||
          Object.keys(customPayload).length === 0;

        const config = {
          headers: {
            ...(isFormData
              ? {}
              : { "Content-Type": "application/json" }),
            ...getAuthHeader(),
          },
          responseType,
          params:
            method === "GET" && !isCustomPayloadEmpty
              ? customPayload
              : undefined,
          data:
            method === "DELETE" && !isCustomPayloadEmpty
              ? customPayload
              : undefined,
        };

        const finalUrl = `${apibaseurl}${customUrl ?? endpoint}`;

        let response: AxiosResponse<any>;

        if (method === "POST") {
          response = await axios.post(
            finalUrl,
            customPayload,
            config,
          );
        } else if (method === "PUT") {
          response = await axios.put(
            finalUrl,
            customPayload,
            config,
          );
        } else if (method === "PATCH") {
          response = await axios.patch(
            finalUrl,
            customPayload,
            config,
          );
        } else if (method === "DELETE") {
          response = await axios.delete(finalUrl, config);
        } else {
          response = await axios.get(finalUrl, config);
        }

        setData(response.data);
        setError(null);

        return response.data;
      } catch (err: any) {
        setError(err);

        if (axios.isAxiosError(err)) {
          const status = err.response?.status;
          const errorCode = err.response?.data?.code;

          const errorMessage =
            err.response?.data?.error ||
            err.response?.data?.message ||
            "";

          const isUnauthorized =
            status === 401 ||
            errorCode === "UNAUTHORIZED" ||
            /invalid or expired token/i.test(errorMessage);

          if (isUnauthorized) {
            /*
             * Login requests must pass the actual backend error
             * to Login.tsx instead of showing Session Expired.
             */
            if (!handleUnauthorized) {
              throw err;
            }

            // Avoid duplicate session-expired alerts.
            if (isSessionExpiredAlertOpen) {
              return undefined;
            }

            isSessionExpiredAlertOpen = true;

            try {
              await Swal.fire({
                icon: "warning",
                title: "Session Expired",
                text: "Your session has expired. Please log in again.",
                confirmButtonText: "OK",
                allowOutsideClick: false,
                allowEscapeKey: false,
                allowEnterKey: true,
              });

              localStorage.removeItem("token");
              localStorage.removeItem("login");
              sessionStorage.removeItem("token");

              window.location.replace("/login");

              return undefined;
            } catch (alertError) {
              console.error(
                "Unable to display session-expired alert:",
                alertError,
              );

              localStorage.removeItem("token");
              localStorage.removeItem("login");
              sessionStorage.removeItem("token");

              window.location.replace("/login");

              return undefined;
            } finally {
              isSessionExpiredAlertOpen = false;
            }
          }

          if (
            status === 500 ||
            err.code === "ERR_NETWORK" ||
            !err.response
          ) {
            const message = !err.response
              ? "Network error. Please check your connection."
              : "Something went wrong on our end. Please try again.";

            console.error(message);

            if (handleGlobalError) {
              navigate("/error-500");
            }
          }
        }

        // Allow the calling component to handle other API errors.
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [
      endpoint,
      method,
      payload,
      navigate,
      handleGlobalError,
      handleUnauthorized,
      responseType,
      auth?.token,
    ],
  );

  useEffect(() => {
    if (autoCall) {
      callApi();
    }
  }, [...dependencies, callApi, autoCall]);

  return {
    data,
    loading,
    error,
    callApi,
  };
}

export default useApi;



// import { useState, useEffect, useCallback } from "react";
// import { useNavigate } from "react-router";
// import { apibaseurl } from "../webservice/Webservice_url";
// import axios, { AxiosResponse } from "axios";
// import { useAuth } from "../context/useAuthContext";
// import Swal from "sweetalert2";

// interface ApiOptions {
//   method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
//   payload?: Record<string, any> | null;
//   autoCall?: boolean;
//   dependencies?: any[];
//   responseType?: "json" | "blob" | "arraybuffer";
//   handleGlobalError?: boolean;
// }

// interface UseApiResult<T> {
//   data: T | null;
//   loading: boolean;
//   error: any;
//   callApi: (
//     payload?: Record<string, any> | FormData,
//     customUrl?: string,
//   ) => Promise<T | undefined>;
// } 
// let isSessionExpiredAlertOpen = false;

// function useApi<T = any>(
//   endpoint: string,
//   {
//     method = "GET",
//     payload = null,
//     autoCall = true,
//     dependencies = [],
//     responseType = "json",
//     handleGlobalError = true,
//   }: ApiOptions = {},
// ): UseApiResult<T> {
//   const [data, setData] = useState<T | null>(null);
//   const [loading, setLoading] = useState<boolean>(autoCall);
//   const [error, setError] = useState<any>(null);

//   const navigate = useNavigate();
//   const { auth } = useAuth();

//   const getAuthHeader = () => {
//     const token = auth?.token;
//     return token ? { Authorization: `Bearer ${token}` } : {};
//   };

//   const callApi = useCallback(
//     async (
//       customPayload: Record<string, any> | FormData | null = payload,
//       customUrl?: string,
//     ): Promise<T | undefined> => {
//       setLoading(true);

//       try {
//         const isFormData = customPayload instanceof FormData;

//         const isCustomPayloadEmpty =
//           !customPayload || Object.keys(customPayload).length === 0;

//         const config = {
//           headers: {
//             ...(isFormData
//               ? {}
//               : { "Content-Type": "application/json" }),
//             ...getAuthHeader(),
//           },
//           responseType,
//           params:
//             method === "GET" && !isCustomPayloadEmpty
//               ? customPayload
//               : undefined,
//           data:
//             method === "DELETE" && !isCustomPayloadEmpty
//               ? customPayload
//               : undefined,
//         };

//         const finalUrl = `${apibaseurl}${customUrl ?? endpoint}`;

//         let response: AxiosResponse<any>;

//         if (method === "POST") {
//           response = await axios.post(finalUrl, customPayload, config);
//         } else if (method === "PUT") {
//           response = await axios.put(finalUrl, customPayload, config);
//         } else if (method === "PATCH") {
//           response = await axios.patch(finalUrl, customPayload, config);
//         } else if (method === "DELETE") {
//           response = await axios.delete(finalUrl, config);
//         } else {
//           response = await axios.get(finalUrl, config);
//         }

//         setData(response.data);
//         setError(null);

//         return response.data;
//       } catch (err: any) {
//         setError(err);

//         if (axios.isAxiosError(err)) {
//           const status = err.response?.status;
//           const errorCode = err.response?.data?.code;

//           const errorMessage =
//             err.response?.data?.error ||
//             err.response?.data?.message ||
//             "";

//           const isUnauthorized =
//             status === 401 ||
//             errorCode === "UNAUTHORIZED" ||
//             /invalid or expired token/i.test(errorMessage);

//           if (isUnauthorized) {
//             // Avoid showing the same alert for multiple failed requests.
//             if (isSessionExpiredAlertOpen) {
//               return undefined;
//             }

//             isSessionExpiredAlertOpen = true;

//             try {
//               await Swal.fire({
//                 icon: "warning",
//                 title: "Session Expired",
//                 text: "Your session has expired. Please log in again.",
//                 confirmButtonText: "OK",
//                 allowOutsideClick: false,
//                 allowEscapeKey: false,
//                 allowEnterKey: true,
//               });

//               // Clear authentication only after the user clicks OK.
//               localStorage.removeItem("token");
//               localStorage.removeItem("login");
//               sessionStorage.removeItem("token");

//               // Redirect after the alert is confirmed.
//               window.location.replace("/login");

//               return undefined;
//             } catch (alertError) {
//               console.error(
//                 "Unable to display session-expired alert:",
//                 alertError,
//               );

//               // Fallback if the alert cannot be displayed.
//               localStorage.removeItem("token");
//               localStorage.removeItem("login");
//               sessionStorage.removeItem("token");

//               window.location.replace("/login");

//               return undefined;
//             }
//           }

//           if (
//             status === 500 ||
//             err.code === "ERR_NETWORK" ||
//             !err.response
//           ) {
//             const message = !err.response
//               ? "Network error. Please check your connection."
//               : "Something went wrong on our end. Please try again.";

//             console.error(message);

//             if (handleGlobalError) {
//               navigate("/error-500");
//             }
//           }
//         }

//         // Allow components to handle other API errors.
//         throw err;
//       } finally {
//         setLoading(false);
//       }
//     },
//     [
//       endpoint,
//       method,
//       payload,
//       navigate,
//       handleGlobalError,
//       responseType,
//       auth?.token,
//     ],
//   );

//   useEffect(() => {
//     if (autoCall) {
//       callApi();
//     }
//   }, [...dependencies, callApi, autoCall]);

//   return { data, loading, error, callApi };
// }

// export default useApi; 